import { useEffect, useRef } from 'react';

/**
 * Código de 6 dígitos com uma caixa por dígito; aceita colar o código inteiro.
 * Usa uma ref com o valor mais recente para não perder dígitos digitados rapidamente
 * (o estado do React só atualiza no próximo render).
 */
export default function CodeInput({ value, onChange, length = 6, autoFocus, onComplete }: {
  value: string;
  onChange: (v: string) => void;
  length?: number;
  autoFocus?: boolean;
  onComplete?: (v: string) => void;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const latest = useRef(value);
  useEffect(() => { latest.current = value; }, [value]);

  const set = (next: string) => {
    const clean = next.replace(/\D/g, '').slice(0, length);
    latest.current = clean;
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
    refs.current[Math.min(clean.length, length - 1)]?.focus();
  };

  return (
    <div className="code-input" onPaste={(e) => { e.preventDefault(); set(e.clipboardData.getData('text')); }}>
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Dígito ${i + 1}`}
          maxLength={length}
          autoFocus={autoFocus && i === 0}
          value={value[i] ?? ''}
          onChange={(e) => {
            const typed = e.target.value.replace(/\D/g, '');
            if (!typed) return;
            // colagem/autopreenchimento do SMS no 1º campo traz o código inteiro
            if (typed.length > 1) return set(typed);
            const cur = latest.current;
            set(cur.slice(0, Math.min(i, cur.length)) + typed);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              e.preventDefault();
              const cur = latest.current;
              set(cur.slice(0, Math.max(0, cur[i] ? i : i - 1)));
            }
          }}
        />
      ))}
    </div>
  );
}
